import { useState, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import api from '../../../../components/utils/api';
import { isFp32ConvertedModelReady, requiredFp32FormatLabel } from '../../../../components/utils/convertedModel';
import Spinner from '../../../../components/template/Spinner';

const getModelTestingPath = (generalModelName, mergeRuleName) => {
    if (!generalModelName || !mergeRuleName) return ""
    const parts = generalModelName.split('_')
    return `${process.env.REACT_APP_MODEL_TESTING_DOMAIN}?db=${parts[0]}&experiment=${parts[1]}&module=${generalModelName}&rule=${mergeRuleName}`
}

const LiveTestPage = () => {
    const [isReady, setIsReady] = useState(false)
    const [isTorch, setIsTorch] = useState(undefined)
    const [loadError, setLoadError] = useState(false)
    const [generalModelName, setGeneralModelName] = useState(null)
    const [mergeRuleName, setMergeRuleName] = useState(null)
    const [isConverting, setIsConverting] = useState(false)
    const [availableModels, setAvailableModels] = useState(null)
    const { generalModelId, mergeRuleId } = useParams();

    useEffect(() => {
        const fetchGeneralModel = async () => {
            try {
                const res = await api.get(`/api/general-models/${generalModelId}`)
                setIsTorch(res.data.model_type === "Torch")
                setGeneralModelName(res.data.name)
            } catch (err) {
                console.error(err)
                setLoadError(true)
            }
        }
        if (generalModelId) {
            fetchGeneralModel()
        }
    }, [generalModelId])

    const fetchIsReady = useCallback(async () => {
        try {
            const res = await api.get(
                `/api/merge-rules/${mergeRuleId}/converted-models/framework-map?is_torch=${isTorch}`
            );
            const modelMap = res.data.converted_model_map;

            if (isTorch) {
                setAvailableModels(modelMap?.onnx_float32?.available_models || []);
            }

            const isReady = isFp32ConvertedModelReady(modelMap, isTorch);

            setIsReady(isReady);
            if (isReady) setIsConverting(false);
        } catch (error) {
            console.error("Error fetching model ready status:", error);
            setIsReady(false);
        }
    }, [mergeRuleId, isTorch])

    useEffect(() => {
        if (mergeRuleId && isTorch !== undefined) {
            fetchIsReady();
        }
    }, [mergeRuleId, isTorch, fetchIsReady])

    // Auto-poll every 10s while converting
    useEffect(() => {
        if (!isConverting) return;
        const interval = setInterval(fetchIsReady, 10000);
        return () => clearInterval(interval);
    }, [isConverting, fetchIsReady])

    const handleConvert = async () => {
        setIsConverting(true)
        try {
            const payload = isTorch
                ? {
                    framework: 'onnx',
                    data_type: 'float32',
                    is_torch: true,
                    models_to_generate: availableModels?.includes('pre') ? ['all', 'pre'] : ['all'],
                }
                : {
                    framework: 'pb',
                    data_type: 'float32',
                    is_torch: false,
                };
            await api.post(`/api/merge-rules/${mergeRuleId}/converted-models`, payload);
        } catch (err) {
            console.error(err)
            setIsConverting(false)
        }
    }

    useEffect(() => {
        const fetchMergeRule = async () => {
            try {
                const res = await api.get(`/api/merge-rules/${mergeRuleId}`)
                setMergeRuleName(res.data.name)
            } catch (err) {
                console.error(err)
            }
        }
        if (mergeRuleId) {
            fetchMergeRule()
        }
    }, [mergeRuleId])

    const isLoading = isTorch === undefined && !loadError
    const modelTestingUrl = getModelTestingPath(generalModelName, mergeRuleName)
    const requiredFormat = requiredFp32FormatLabel(isTorch)

    if (loadError) {
        return (
            <div className='p-1'>
                <p className='text-sm text-red-500 py-8'>Failed to load model information.</p>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className='p-1'>
                <div className='flex items-center gap-2 text-gray-500 py-8'>
                    <Spinner />
                    <span className='text-sm'>Loading...</span>
                </div>
            </div>
        )
    }

    return (
        <div className='p-1'>
            <div className='flex justify-between items-center mb-4'>
                <div className='font-bold text-base text-left'>Live Test</div>
                <button type='button' className='text-sm text-gray-400' onClick={fetchIsReady}>Refresh <FontAwesomeIcon icon={faRotate} /></button>
            </div>

            <div className='flex flex-col items-center py-10'>
                {/* Icon */}
                <div className={`w-24 h-24 rounded-full flex items-center justify-center ${
                    isReady ? 'bg-green-100' : 'bg-orange-100'
                }`}>
                    {isReady ? (
                        <svg width="48" height="48" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="256" cy="256" r="224" stroke="#34d399" strokeWidth="32" fill="white" />
                            <polyline points="160,260 228,328 352,196" stroke="#34d399" strokeWidth="36" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        </svg>
                    ) : (
                        <svg width="48" height="48" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="256" cy="256" r="224" stroke="#fb923c" strokeWidth="32" fill="none" />
                            <line x1="256" y1="152" x2="256" y2="312" stroke="#fb923c" strokeWidth="40" strokeLinecap="round" />
                            <circle cx="256" cy="376" r="24" fill="#fb923c" />
                        </svg>
                    )}
                </div>

                {/* Title */}
                <h2 className='text-xl font-bold text-gray-800 mt-4'>
                    {isReady ? 'Model is Ready' : 'Conversion Required'}
                </h2>

                {/* Format badge */}
                <div className='mt-3 bg-gray-100 rounded-full px-4 py-1.5 flex items-center gap-2'>
                    <span className='text-xs font-semibold text-gray-400 uppercase tracking-wide'>Format</span>
                    <span className='text-sm font-semibold text-gray-700'>{requiredFormat}</span>
                </div>

                {/* Action button */}
                <div className='mt-6'>
                    {isReady ? (
                        modelTestingUrl ? (
                            <a
                                href={modelTestingUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-medium inline-flex items-center justify-center h-12 w-40 text-sm text-violet-500 bg-violet-100 border border-violet-400 rounded hover:bg-violet-200'
                            >
                                Model Testing
                            </a>
                        ) : (
                            <button
                                disabled
                                className='font-medium h-12 w-40 text-sm border rounded flex items-center justify-center border-gray-300 bg-gray-50 text-gray-400 cursor-not-allowed'
                            >
                                Model Testing
                            </button>
                        )
                    ) : (
                        <button
                            type='button'
                            className='font-medium h-12 w-40 text-sm text-violet-500 bg-violet-100 border border-violet-400 rounded hover:bg-violet-200 disabled:border-gray-300 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed'
                            onClick={handleConvert}
                            disabled={isConverting}
                        >
                            {isConverting ? <div className='flex items-center justify-center gap-2'><Spinner /><span className='text-xs'>Converting...</span></div> : 'Convert'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default LiveTestPage
